import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { signInWithEmailAndPassword } from "firebase/auth";
import { auth } from "../../../../../../../firebaseConfig";
import { useUserStore } from "../../../../../../../store/userStore";
import type { VehicleSocket } from "../../../../../../../store/admin/vehiclesSocketStore";
import api, { isAxiosClientTimeout } from "../../../../../../../utils/api";
import {
  useDeviceCommandsStore,
  getCommandStatusLabel,
  COMMAND_STATUS_COLORS,
  isSuccessStatus,
  type CommandStatus,
  type SocketCommand,
} from "../../../../../../../store/admin/deviceCommandsStore";
import { useEscapeKey } from "../../../../../../../hooks/useEscapeKey";

interface CommandResponse {
  ok: boolean;
  command: {
    request_id: string;
    device_id: string;
    imei?: string;
    company_id?: string;
    vehicle_id?: string;
    remolque_id?: string;
    brand_id?: string;
    brand?: string;
    model?: string;
    command_doc_id?: string;
    command_code: string;
    command_name?: string;
    status: CommandStatus;
    error?: string;
    raw_request?: Record<string, unknown>;
    raw_response?: Record<string, unknown>;
    sent_at?: number;
    finished_at?: number;
    created_at?: number;
    updated_at?: number;
  };
  wait_timeout?: boolean;
}

interface AddCustomCommandModalProps {
  vehicle: VehicleSocket;
  isDarkMode: boolean;
  onClose: () => void;
  onSuccess: () => void;
  onSendCommandClientTimeout?: () => void;
}

type ViewState = "form" | "sending" | "result";
type CommandChannel = "gprs" | "sms";

export default function AddCustomCommandModal({
  vehicle,
  isDarkMode,
  onClose,
  onSuccess,
  onSendCommandClientTimeout,
}: AddCustomCommandModalProps) {
  useEscapeKey(onClose);
  const { t } = useTranslation("lite");
  const user = useUserStore((state) => state.user);

  const [viewState, setViewState] = useState<ViewState>("form");
  const [channel, setChannel] = useState<CommandChannel>("gprs");
  const [authPassword, setAuthPassword] = useState("");
  const [smsPassword, setSmsPassword] = useState("");
  const [commandMessage, setCommandMessage] = useState("");
  const [commandName, setCommandName] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [commandResponse, setCommandResponse] = useState<CommandResponse | null>(null);
  const [apiError, setApiError] = useState<string | null>(null);
  const [currentRequestId, setCurrentRequestId] = useState<string | null>(null);

  const addCommand = useDeviceCommandsStore((state) => state.addCommand);
  const commandsByDevice = useDeviceCommandsStore((state) => state.commandsByDevice);

  const currentCommandFromStore = useMemo<SocketCommand | null>(() => {
    if (!currentRequestId || !vehicle?.id) return null;
    const deviceCommands = commandsByDevice[vehicle.id] || [];
    return deviceCommands.find((cmd) => cmd.request_id === currentRequestId) || null;
  }, [commandsByDevice, vehicle?.id, currentRequestId]);

  // Theme classes
  const bgColor = isDarkMode ? "bg-dark-bg" : "bg-white";
  const textColor = isDarkMode ? "text-gray-300" : "text-gray-700";
  const textSecondary = isDarkMode ? "text-gray-400" : "text-gray-500";
  const borderColor = isDarkMode ? "border-gray-700" : "border-gray-200";
  const inputBg = isDarkMode ? "bg-[#252525]" : "bg-white";
  const cardBg = isDarkMode ? "bg-dark-card" : "bg-gray-50";

  const getStatusColor = (status: CommandStatus) => {
    const colors = COMMAND_STATUS_COLORS[status];
    if (!colors) return { bg: "bg-gray-100", text: "text-gray-700" };
    return isDarkMode
      ? { bg: colors.darkBg, text: colors.darkText }
      : { bg: colors.bg, text: colors.text };
  };

  const handleSendCommand = async () => {
    if (!authPassword.trim() || !commandMessage.trim()) {
      setError(t("customCommand.fieldsRequired"));
      return;
    }

    if (channel === "sms" && !smsPassword.trim()) {
      setError(t("deviceCommands.smsPasswordRequired"));
      return;
    }

    setIsSubmitting(true);
    setError(null);
    setApiError(null);
    setCommandResponse(null);
    setCurrentRequestId(null);

    try {
      const email = user?.email;
      if (!email) {
        setError(t("deviceCommands.noEmail"));
        setIsSubmitting(false);
        return;
      }

      await signInWithEmailAndPassword(auth, email, authPassword);

      setViewState("sending");

      const resolvedName = commandName.trim() || "Comando personalizado";

      const response =
        channel === "sms"
          ? await api.post("/sendCustomCommandSms", {
              deviceId: vehicle.id,
              imei: vehicle.imei,
              text: commandMessage,
              password_sms: smsPassword,
              command_name: resolvedName,
              created_by_user_id: user?.id,
            })
          : await api.post("/sendCustomCommand", {
              imei: vehicle.imei,
              text: commandMessage,
              command_name: resolvedName,
              response_expected: false,
              await_ms: 8000,
              created_by_user_id: user?.id,
            });

      const data: CommandResponse = response.data;
      setCommandResponse(data);

      if (data.command?.request_id) {
        setCurrentRequestId(data.command.request_id);

        addCommand({
          device_id: data.command.device_id || vehicle.id,
          company_id: data.command.company_id || null,
          vehicle_id: data.command.vehicle_id || null,
          remolque_id: data.command.remolque_id || null,
          command: {
            request_id: data.command.request_id,
            imei: data.command.imei,
            brand_id: data.command.brand_id,
            brand: data.command.brand,
            model: data.command.model,
            command_doc_id: data.command.command_doc_id,
            command_code: data.command.command_code,
            command_name: data.command.command_name || resolvedName,
            status: data.command.status,
            error: data.command.error,
            raw_request: data.command.raw_request,
            raw_response: data.command.raw_response,
            sent_at: data.command.sent_at,
            finished_at: data.command.finished_at,
          },
        });
      }

      setViewState("result");
    } catch (err: unknown) {
      console.error("Error sending custom command:", err);
      const firebaseErr = err as { code?: string };
      if (
        firebaseErr.code === "auth/wrong-password" ||
        firebaseErr.code === "auth/invalid-credential"
      ) {
        setError(t("deviceCommands.wrongPassword"));
        setIsSubmitting(false);
      } else if (isAxiosClientTimeout(err) && onSendCommandClientTimeout) {
        onSendCommandClientTimeout();
      } else {
        const anyErr = err as { response?: { data?: { message?: string } }; message?: string };
        setApiError(
          anyErr.response?.data?.message ||
            anyErr.message ||
            "Error al enviar comando",
        );
        setViewState("result");
      }
    }
  };

  // Form view
  if (viewState === "form") {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className={`${bgColor} rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto border ${borderColor} shadow-2xl`}>
          {/* Header */}
          <div className={`flex items-center justify-between p-4 border-b ${borderColor}`}>
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-lg bg-blue-500 flex items-center justify-center">
                <i className="ri-terminal-line text-white text-lg"></i>
              </div>
              <h2 className={`font-semibold text-lg ${textColor}`}>
                {t("customCommand.title")}
              </h2>
            </div>
            <button
              onClick={onClose}
              className={`p-2 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} rounded-lg transition-colors`}
            >
              <i className={`ri-close-line text-xl ${textColor}`}></i>
            </button>
          </div>

          {/* Form */}
          <div className="p-6 space-y-4">
            {/* Vehicle info */}
            <div className={`${cardBg} rounded-lg p-3 flex items-center gap-3`}>
              <i className="ri-truck-line text-primary-500 text-xl"></i>
              <div>
                <p className={`text-xs ${textSecondary}`}>{t("customCommand.sendingTo")}</p>
                <p className={`font-medium ${textColor}`}>{vehicle.vehicle.name}</p>
              </div>
            </div>

            {error && (
              <div className="bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400 px-4 py-3 rounded-lg text-sm">
                {error}
              </div>
            )}

            {/* Send channel */}
            <div className="flex flex-col">
              <label className={`text-sm font-medium ${textColor} mb-1`}>
                {t("deviceCommands.channel")}
              </label>
              <div className="flex gap-3">
                <label className={`flex-1 flex items-center justify-center gap-2 cursor-pointer rounded-lg border px-4 py-2.5 transition-colors ${channel === "gprs" ? "border-primary-500 bg-primary-500/10" : borderColor}`}>
                  <input
                    type="radio"
                    name="customCommandChannel"
                    value="gprs"
                    checked={channel === "gprs"}
                    onChange={() => { setChannel("gprs"); setError(null); }}
                    className="w-4 h-4 accent-primary-500"
                  />
                  <span className={textColor}>{t("deviceCommands.channelGprs")}</span>
                </label>
                <label className={`flex-1 flex items-center justify-center gap-2 cursor-pointer rounded-lg border px-4 py-2.5 transition-colors ${channel === "sms" ? "border-primary-500 bg-primary-500/10" : borderColor}`}>
                  <input
                    type="radio"
                    name="customCommandChannel"
                    value="sms"
                    checked={channel === "sms"}
                    onChange={() => { setChannel("sms"); setError(null); }}
                    className="w-4 h-4 accent-primary-500"
                  />
                  <span className={textColor}>{t("deviceCommands.channelSms")}</span>
                </label>
              </div>
            </div>

            {/* Authorization Password */}
            <div className="flex flex-col">
              <label className={`text-sm font-medium ${textColor} mb-1`}>
                <i className="ri-shield-keyhole-line mr-1 text-yellow-500"></i>
                {t("customCommand.authPassword")} <span className="text-red-500">*</span>
              </label>
              <input
                type="password"
                value={authPassword}
                onChange={(e) => setAuthPassword(e.target.value)}
                placeholder={t("customCommand.authPasswordPlaceholder")}
                className={`w-full px-4 py-2.5 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
              />
              <p className={`text-xs ${textSecondary} mt-1`}>
                {t("customCommand.authPasswordHelp")}
              </p>
            </div>

            {channel === "sms" && (
              <div className="flex flex-col">
                <label className={`text-sm font-medium ${textColor} mb-1`}>
                  <i className="ri-shield-keyhole-line mr-1 text-yellow-500"></i>
                  {t("deviceCommands.smsPassword")} <span className="text-red-500">*</span>
                </label>
                <input
                  type="password"
                  value={smsPassword}
                  onChange={(e) => { setSmsPassword(e.target.value); setError(null); }}
                  placeholder={t("deviceCommands.smsPasswordPlaceholder")}
                  className={`w-full px-4 py-2.5 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
                />
                <p className={`text-xs ${textSecondary} mt-1`}>
                  {t("deviceCommands.smsPasswordHelp")}
                </p>
              </div>
            )}

            {/* Command name (optional label) */}
            <div className="flex flex-col">
              <label className={`text-sm font-medium ${textColor} mb-1`}>
                <i className="ri-price-tag-3-line mr-1 text-blue-500"></i>
                {t("customCommand.commandName")}
              </label>
              <input
                type="text"
                value={commandName}
                onChange={(e) => setCommandName(e.target.value)}
                placeholder={t("customCommand.commandNamePlaceholder")}
                className={`w-full px-4 py-2.5 rounded-lg border ${borderColor} ${inputBg} ${textColor} focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent`}
              />
              <p className={`text-xs ${textSecondary} mt-1`}>
                {t("customCommand.commandNameHelp")}
              </p>
            </div>

            {/* Command to send - Console style */}
            <div className="flex flex-col">
              <label className={`text-sm font-medium ${textColor} mb-1`}>
                <i className={`ri-terminal-line mr-1 ${isDarkMode ? "text-green-400" : "text-green-600"}`}></i>
                {t("customCommand.commandToSend")} <span className="text-red-500">*</span>
              </label>
              <textarea
                value={commandMessage}
                onChange={(e) => setCommandMessage(e.target.value)}
                placeholder={t("customCommand.commandPlaceholder")}
                rows={3}
                className={`w-full px-5 py-4 rounded-lg bg-[#1e1e1e] font-mono text-sm text-green-400 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-green-500 resize-none border ${isDarkMode ? "border-gray-600" : "border-gray-300"}`}
              />
              <p className={`text-xs ${textSecondary} mt-1`}>
                {t("customCommand.commandHelp")}
              </p>
            </div>

            {/* Warning */}
            <div className={`flex items-start gap-2 p-3 rounded-lg ${isDarkMode ? "bg-yellow-900/20" : "bg-yellow-50"} border ${isDarkMode ? "border-yellow-800" : "border-yellow-200"}`}>
              <i className="ri-alert-line text-yellow-500 mt-0.5"></i>
              <p className={`text-xs ${isDarkMode ? "text-yellow-400" : "text-yellow-700"}`}>
                {t("customCommand.warning")}
              </p>
            </div>

            {/* Buttons */}
            <div className="flex justify-end gap-3 pt-2">
              <button
                onClick={onClose}
                className={`px-6 py-2.5 rounded-lg border ${borderColor} ${textColor} hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors font-medium`}
              >
                {t("deviceCommands.cancel")}
              </button>
              <button
                onClick={handleSendCommand}
                disabled={isSubmitting || !authPassword.trim() || !commandMessage.trim() || (channel === "sms" && !smsPassword.trim())}
                className="px-6 py-2.5 rounded-lg bg-blue-500 text-white font-medium hover:bg-blue-400 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {isSubmitting ? (
                  <>
                    <i className="ri-loader-4-line animate-spin"></i>
                    {t("customCommand.verifying")}
                  </>
                ) : (
                  <>
                    <i className="ri-send-plane-line"></i>
                    {t("customCommand.sendCommand")}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Sending state
  if (viewState === "sending") {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className={`${bgColor} rounded-xl max-w-md w-full p-8 border ${borderColor} shadow-2xl`}>
          <div className="flex flex-col items-center justify-center py-8">
            <div className="relative w-20 h-20 mb-6">
              <div className="absolute inset-0 rounded-full border-4 border-blue-500/30"></div>
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-blue-500 animate-spin"></div>
              <div className="absolute inset-0 flex items-center justify-center">
                <i className="ri-terminal-line text-3xl text-blue-500"></i>
              </div>
            </div>
            <h3 className={`font-semibold text-lg ${textColor} mb-2`}>
              {t("customCommand.sending")}
            </h3>
            <p className={`text-center ${textSecondary} text-sm`}>
              {t("deviceCommands.pleaseWait")}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Result state
  if (viewState === "result") {
    const cmdStatus = currentCommandFromStore?.status || commandResponse?.command?.status;
    const isSuccess = cmdStatus ? isSuccessStatus(cmdStatus) : false;
    const backendError = currentCommandFromStore?.error || commandResponse?.command?.error;
    const cmdError = isSuccess ? null : (backendError || apiError);
    const requestId = currentCommandFromStore?.request_id || commandResponse?.command?.request_id;
    const statusColor = cmdStatus ? getStatusColor(cmdStatus) : null;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className={`${bgColor} rounded-xl max-w-md w-full max-h-[90vh] overflow-y-auto border ${borderColor} shadow-2xl`}>
          {/* Header */}
          <div className={`flex items-center justify-between p-4 border-b ${borderColor}`}>
            <div className="flex items-center gap-3">
              <div className={`w-8 h-8 rounded-lg ${isSuccess ? "bg-green-500" : "bg-red-500"} flex items-center justify-center`}>
                <i className={`${isSuccess ? "ri-check-line" : "ri-close-line"} text-white text-lg`}></i>
              </div>
              <h2 className={`font-semibold text-lg ${textColor}`}>
                {isSuccess
                  ? t("deviceCommands.commandSent")
                  : t("deviceCommands.commandFailed")
                }
              </h2>
            </div>
            <button
              onClick={onClose}
              className={`p-2 ${isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"} rounded-lg transition-colors`}
            >
              <i className={`ri-close-line text-xl ${textColor}`}></i>
            </button>
          </div>

          <div className="p-6">
            {/* Nombre del comando */}
            <div className="text-center mb-4">
              <p className={`text-xs ${textSecondary} mb-1`}>
                {t("deviceCommands.command")}
              </p>
              <h3 className={`font-bold text-lg ${textColor}`}>
                {commandName.trim() || "Comando personalizado"}
              </h3>
            </div>

            {/* Estado del comando */}
            {cmdStatus && (
              <div className="flex justify-center mb-4">
                <span className={`px-4 py-2 rounded-lg text-sm font-medium ${statusColor?.bg} ${statusColor?.text}`}>
                  {getCommandStatusLabel(cmdStatus)}
                </span>
              </div>
            )}

            {/* Error message */}
            {cmdError && (
              <div className={`${isDarkMode ? "bg-red-900/30 text-red-400" : "bg-red-100 text-red-700"} px-3 py-2 rounded-lg text-sm mb-4 text-center`}>
                Error: <span className="font-bold">{cmdError}</span>
              </div>
            )}

            {/* Request ID */}
            {requestId && (
              <div className={`p-3 rounded-lg ${isDarkMode ? "bg-dark-surface" : "bg-gray-50"} mb-4`}>
                <p className={`text-[10px] ${textSecondary} mb-1`}>Request ID</p>
                <p className={`text-xs ${textColor} font-mono break-all`}>
                  {requestId}
                </p>
              </div>
            )}

            {/* Wait timeout indicator */}
            {commandResponse?.wait_timeout && (
              <div className={`${isDarkMode ? "bg-yellow-900/30 text-yellow-400" : "bg-yellow-100 text-yellow-700"} px-3 py-2 rounded-lg text-xs mb-4 text-center`}>
                {t("deviceCommands.waitTimeout")}
              </div>
            )}

            {/* Info adicional según el estado */}
            {cmdStatus && (
              <div className={`text-center ${textSecondary} text-xs mb-4`}>
                {cmdStatus === "pending_route" && t("deviceCommands.statusPendingRoute")}
                {cmdStatus === "queued_for_route" && t("deviceCommands.statusQueuedForRoute")}
                {cmdStatus === "queued" && t("deviceCommands.statusQueued")}
                {cmdStatus === "sending" && t("deviceCommands.statusSending")}
                {cmdStatus === "sent" && t("deviceCommands.statusSent")}
                {cmdStatus === "ack" && t("deviceCommands.statusAck")}
                {cmdStatus === "timeout" && t("deviceCommands.statusTimeout")}
                {cmdStatus === "offline" && t("deviceCommands.deviceOffline")}
                {cmdStatus === "unsupported" && t("deviceCommands.commandUnsupported")}
                {cmdStatus === "rejected" && t("deviceCommands.statusRejected")}
                {cmdStatus === "error" && t("deviceCommands.statusError")}
                {cmdStatus === "expired" && t("deviceCommands.statusExpired")}
                {cmdStatus === "write_failed" && t("deviceCommands.statusWriteFailed")}
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-3 mt-6">
              {!isSuccess && (
                <button
                  onClick={() => {
                    setViewState("form");
                    setIsSubmitting(false);
                    setApiError(null);
                    setCommandResponse(null);
                    setCurrentRequestId(null);
                  }}
                  className={`flex-1 px-6 py-2.5 rounded-lg border ${borderColor} ${textColor} ${isDarkMode ? "hover:bg-dark-surface" : "hover:bg-gray-100"} transition-colors font-medium`}
                >
                  {t("deviceCommands.tryAgain")}
                </button>
              )}
              <button
                onClick={() => {
                  onSuccess();
                  onClose();
                }}
                className={`${isSuccess ? "w-full" : "flex-1"} px-6 py-2.5 rounded-lg bg-primary-500 text-black font-medium hover:bg-primary-400 transition-colors`}
              >
                {t("deviceCommands.close")}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return null;
}
